import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ErrorStateMatcher } from '@angular/material/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type {
	RubricSessionElement,
	RubricSessionElementJustification
} from '@go-modules/interfaces/rubrics/rubric';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

interface DisplayValue {
	label: string;
	value: number | null;
}

type AutoGradeStatus = 'decreased' | 'increased' | 'retained' | 'unchanged';

@Component({
	selector: 'ngx-auto-graded-justification',
	standalone: true,
	imports: [
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		CdkTextareaAutosize,
		TranslateModule
	],
	templateUrl: './auto-graded-justification.component.html',
	styleUrls: ['./auto-graded-justification.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoGradedJustificationComponent {
	private readonly translate = inject(TranslateService);

	public readonly element = input.required<RubricSessionElement>();
	public readonly justification = input<RubricSessionElementJustification | null>(null);
	public readonly editable = input(false);
	public readonly isInstructor = input(false);
	public readonly validationError = input<string | null>(null);
	public readonly validationErrorId = input('');
	public readonly instructorCommentChange = output<string | null>();
	public readonly validationErrorStateMatcher: ErrorStateMatcher = {
		isErrorState: () => this.validationError() != null
	};

	public readonly evidence = computed(() => this.justification()?.evidence?.slice(0, 4) ?? []);
	public readonly status = computed<AutoGradeStatus>(() => {
		const element = this.element();
		if (element.original_score == null) {
			return 'unchanged';
		}

		const original = this.displayValue(true);
		const current = this.displayValue(false);
		if (original.value === current.value) {
			return 'retained';
		}

		return current.value > original.value ? 'increased' : 'decreased';
	});
	public readonly statusSummary = computed(() => {
		const element = this.element();
		const original = this.displayValue(true);
		const current = this.displayValue(false);
		if (element.original_score == null) {
			return this.translate.instant('rubric-feedback-viewer_auto-grade-status', {
				current: current.value
			});
		}

		if (this.status() === 'retained') {
			const key = this.isInstructor()
				? 'rubric-feedback-viewer_auto-grade-status-retained-instructor'
				: 'rubric-feedback-viewer_auto-grade-status-retained';
			return this.translate.instant(key, {original: original.label});
		}

		const key = this.isInstructor()
			? 'rubric-feedback-viewer_auto-grade-status-changed-instructor'
			: 'rubric-feedback-viewer_auto-grade-status-changed';

		const originalLabel = this.translate.instant(original.label);
		const currentLabel = this.translate.instant(current.label);
		return this.translate.instant(key, {
			original: originalLabel,
			current: currentLabel
		});
	});

	public formatTimestamp (timestampMs: number): string {
		const totalSeconds = Math.floor(timestampMs / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = `${totalSeconds % 60}`.padStart(2, '0');
		return `${minutes}:${seconds}`;
	}

	private displayValue (original: boolean): DisplayValue {
		const element = this.element();
		const originalScore = element.original_score;
		if (Array.isArray(element.options)) {
			const optionId = original ? originalScore?.rubric_element_option_id : element.option;
			const option = element.options.find((candidate) => candidate.id === optionId);
			return {
				label: option?.label ?? '',
				value: option == null ? null : Number(option.value)
			};
		}

		if (element.type === 'checkbox') {
			const value = Number(original ? originalScore?.value : element.points);
			return {
				label: value === 0 ? 'Not selected' : element.label ?? '',
				value
			};
		}

		const value = original ? originalScore?.value : element.points ?? element.value;
		return {label: `${value ?? ''}`, value: value == null ? null : Number(value)};
	}
}
